import React from 'react';
import LiveDashboard from '../components/mockups/LiveDashboard';
import CRMHub from '../components/mockups/CRMHub';
import WorkflowCanvas from '../components/mockups/WorkflowCanvas';
import MobileFintech from '../components/mockups/MobileFintech';
import LogisticsMap from '../components/mockups/LogisticsMap';
import EcommerceAdmin from '../components/mockups/EcommerceAdmin';
import SaaSPlatform from '../components/mockups/SaaSPlatform';
import AIAssistant from '../components/mockups/AIAssistant';
import ChatbotWidget from '../components/mockups/ChatbotWidget';
import EgovPortal from '../components/mockups/EgovPortal';
import WebPlatform from '../components/mockups/WebPlatform';
import CloudArchitecture from '../components/mockups/CloudArchitecture';
import SecurityOps from '../components/mockups/SecurityOps';

export interface Project {
  id: string;
  number: string;
  title: string;
  description: string;
  tags: string[];
  stack: string[];
  span: 'large' | 'standard';
  mockup: React.ReactNode;
  accent: string;
  featured?: boolean;
}

export const projects: Project[] = [
  {
    id: 'ops-live',
    number: '01',
    title: 'Dashboard Ops Temps Réel',
    description: 'Supervision multi-sites avec KPI live, alertes WebSocket et analyse d\'activité heatmap.',
    tags: ['Temps réel', 'WebSocket', 'Analytics'],
    stack: ['React', 'Node.js', 'WebSocket', 'PostgreSQL'],
    span: 'large',
    mockup: <LiveDashboard />,
    accent: 'var(--blue)',
    featured: true,
  },
  {
    id: 'crm-hub',
    number: '02',
    title: 'Hub CRM Unifié',
    description: 'Synchronisation bidirectionnelle entre Salesforce, HubSpot, Zoho et Odoo en temps réel.',
    tags: ['Intégration CRM', 'API', 'Sync'],
    stack: ['Node.js', 'REST', 'Salesforce API', 'Kafka'],
    span: 'standard',
    mockup: <CRMHub />,
    accent: 'var(--gold-dark)',
  },
  {
    id: 'workflow',
    number: '03',
    title: 'Moteur d\'Automatisation',
    description: 'Builder visuel de workflows avec triggers, conditions et actions exécutées en cascade.',
    tags: ['Automatisation', 'Workflows', 'No-code'],
    stack: ['React Flow', 'n8n', 'Redis', 'Temporal'],
    span: 'standard',
    mockup: <WorkflowCanvas />,
    accent: '#5E35B1',
  },
  {
    id: 'saas',
    number: '04',
    title: 'Plateforme SaaS Multi-tenant',
    description: 'Plateforme web complète avec gestion multi-workspace, facturation automatique et gestion d\'équipe.',
    tags: ['Plateforme Web', 'SaaS', 'Multi-tenant'],
    stack: ['Next.js', 'Stripe', 'Clerk', 'PostgreSQL'],
    span: 'standard',
    mockup: <SaaSPlatform />,
    accent: 'var(--blue-dark)',
  },
  {
    id: 'ai-assistant',
    number: '05',
    title: 'Assistant IA & RAG',
    description: 'Copilot IA connecté à votre base documentaire: réponses sourcées, streaming et extraction intelligente.',
    tags: ['IA & LLM', 'RAG', 'Assistant'],
    stack: ['LLM API', 'Embeddings', 'LangChain', 'Vector DB'],
    span: 'standard',
    mockup: <AIAssistant />,
    accent: '#5E35B1',
    featured: true,
  },
  {
    id: 'chatbot',
    number: '06',
    title: 'Chatbot Support 24/7',
    description: 'Agent conversationnel intégré à votre site: réponses instantanées, suggestions et transfert humain.',
    tags: ['IA & LLM', 'Chatbot', 'Support'],
    stack: ['LLM API', 'React', 'WebSocket', 'Helpdesk'],
    span: 'standard',
    mockup: <ChatbotWidget />,
    accent: 'var(--blue)',
    featured: true,
  },
  {
    id: 'mobile-fin',
    number: '07',
    title: 'App Mobile Fintech',
    description: 'Application bancaire mobile avec carte virtuelle, tracking de dépenses et notifications instantanées.',
    tags: ['Mobile', 'Fintech', 'Temps réel'],
    stack: ['React Native', 'TypeScript', 'Firebase'],
    span: 'standard',
    mockup: <MobileFintech />,
    accent: 'var(--blue-dark)',
  },
  {
    id: 'logistics',
    number: '08',
    title: 'Tracking Logistique Live',
    description: 'Supervision de flotte en temps réel: GPS, ETA dynamique, alertes retard et routing intelligent.',
    tags: ['Temps réel', 'GPS', 'Logistique'],
    stack: ['Mapbox', 'WebSocket', 'Go', 'Redis'],
    span: 'large',
    mockup: <LogisticsMap />,
    accent: '#2E7D32',
  },
  {
    id: 'ecom-erp',
    number: '09',
    title: 'E-commerce + ERP',
    description: 'Plateforme e-commerce avec synchronisation continue des stocks, commandes et facturation vers l\'ERP.',
    tags: ['Plateforme Web', 'Intégration', 'Automatisation'],
    stack: ['Next.js', 'Stripe', 'SAP', 'PostgreSQL'],
    span: 'large',
    mockup: <EcommerceAdmin />,
    accent: '#B8922A',
  },
  {
    id: 'egov-portal',
    number: '10',
    title: 'Portail E-Gouvernement',
    description: 'Portail citoyen multilingue pour démarches administratives: passeport, état civil, impôts, santé. Interface FR/AR et suivi temps réel.',
    tags: ['E-Gouvernement', 'Plateforme Web', 'Multilingue'],
    stack: ['Next.js', 'Keycloak', 'PostgreSQL', 'API Gov'],
    span: 'standard',
    mockup: <EgovPortal />,
    accent: 'var(--blue)',
  },
  {
    id: 'web-platform',
    number: '11',
    title: 'Plateforme Web Multilingue',
    description: 'Site institutionnel trilingue (FR/EN/AR) avec CMS, support RTL complet et gestion éditoriale multi-équipes.',
    tags: ['Plateforme Web', 'Multilingue', 'CMS'],
    stack: ['Next.js', 'Strapi', 'i18n', 'CloudFront'],
    span: 'standard',
    mockup: <WebPlatform />,
    accent: 'var(--gold-dark)',
  },
  {
    id: 'cloud-infra',
    number: '12',
    title: 'Architecture Cloud Sécurisée',
    description: 'Infrastructure cloud multi-régions avec auto-scaling, CDN, failover et monitoring 24/7. Uptime 99.98% garanti.',
    tags: ['Cloud', 'Infrastructure', 'DevOps'],
    stack: ['AWS', 'Terraform', 'Kubernetes', 'CloudFront'],
    span: 'standard',
    mockup: <CloudArchitecture />,
    accent: '#2E7D32',
  },
  {
    id: 'security-ops',
    number: '13',
    title: 'SOC · Security Operations',
    description: 'Centre de supervision sécurité 24/7: détection d\'intrusions, WAF, threat intelligence et réponse automatisée aux incidents.',
    tags: ['Cybersécurité', 'SOC', 'Monitoring'],
    stack: ['SIEM', 'Splunk', 'Falco', 'Cloudflare WAF'],
    span: 'standard',
    mockup: <SecurityOps />,
    accent: '#C62828',
  },
];

export const featuredProjects = projects.filter((p) => p.featured);

/* ── Pillars (shared between Teaser and Page) ── */
const IconBolt = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><path d="M8 1L2 8h4l-1 5 6-7H7l1-5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>
);
const IconLink = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><path d="M6 8l2-2m-3 5a3 3 0 01-3-3 3 3 0 013-3h2m3-2a3 3 0 013 3 3 3 0 01-3 3H8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>
);
const IconCog = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.3"/><path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.8 2.8l1.4 1.4M9.8 9.8l1.4 1.4M2.8 11.2l1.4-1.4M9.8 4.2l1.4-1.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
);
const IconAI = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
    <path d="M7 1l1.6 3.8L12.5 6l-3.2 2.6.7 3.4L7 10l-3 2L4.7 8.6 1.5 6l3.9-1.2L7 1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

export const pillars = [
  {
    icon: <IconBolt />,
    title: 'Dashboards temps réel',
    desc: 'Supervision live avec WebSocket, KPI qui s\'actualisent et alertes instantanées.',
    bullets: ['WebSocket & SSE', 'KPI & sparklines live', 'Alertes & seuils'],
    color: 'var(--blue)',
  },
  {
    icon: <IconLink />,
    title: 'Intégrations CRM & SI',
    desc: 'Synchronisation avec Salesforce, HubSpot, Odoo, SAP et vos systèmes internes.',
    bullets: ['Salesforce / HubSpot', 'Odoo / SAP / ERP', 'API REST & webhooks'],
    color: 'var(--gold-dark)',
  },
  {
    icon: <IconCog />,
    title: 'Automatisation',
    desc: 'Workflows visuels, triggers et RPA pour éliminer les tâches répétitives.',
    bullets: ['Workflows n8n / Temporal', 'Triggers & conditions', 'RPA & scripts'],
    color: '#2E7D32',
  },
  {
    icon: <IconAI />,
    title: 'IA & LLM',
    desc: 'Assistants IA, RAG documentaire et chatbots connectés à vos données.',
    bullets: ['LLM & IA générative', 'RAG & embeddings', 'Chatbots & copilots'],
    color: '#5E35B1',
  },
];
